/**
 * ConfidenceBadge Component (CCE-005)
 * 
 * Displays classification confidence level with color-coded badge.
 * Uses Tailwind color tokens (AC #14) for accessibility compliance.
 * 
 * Confidence Thresholds:
 * - HIGH (green): ≥80%
 * - MEDIUM (yellow): 50-79%
 * - LOW (red): <50%
 * 
 * Created: February 3, 2026
 * Epic: CCE - Content Classification Enhancement
 */

import React from 'react';

export interface ConfidenceBadgeProps {
  confidence: number; // 0.0-1.0
  size?: 'sm' | 'md' | 'lg';
}

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ 
  confidence, 
  size = 'md' 
}) => {
  // Convert 0.0-1.0 to percentage
  const percentage = Math.round(confidence * 100);
  
  // Determine confidence level and color class
  const getConfidenceLevel = (): 'HIGH' | 'MEDIUM' | 'LOW' => {
    if (confidence >= 0.8) return 'HIGH';
    if (confidence >= 0.5) return 'MEDIUM';
    return 'LOW';
  };
  
  const level = getConfidenceLevel();
  
  // Tailwind color tokens (AC #14 - no hardcoded hex)
  const getColorClass = (): string => {
    if (confidence >= 0.8) {
      return 'bg-green-100 text-green-800 border-green-300';
    }
    if (confidence >= 0.5) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
    return 'bg-red-100 text-red-800 border-red-300';
  };
  
  // Size classes
  const getSizeClass = (): string => {
    switch (size) {
      case 'sm':
        return 'text-xs px-2 py-0.5';
      case 'lg':
        return 'text-base px-3 py-1.5';
      case 'md':
      default:
        return 'text-sm px-2.5 py-1';
    }
  };
  
  const colorClass = getColorClass();
  const sizeClass = getSizeClass();
  
  return (
    <span
      className={`inline-flex items-center font-medium rounded border ${colorClass} ${sizeClass}`}
      aria-label={`Confidence: ${percentage}% (${level})`}
      role="status"
    >
      {level} {percentage}%
    </span>
  );
};

export default ConfidenceBadge;
